import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';

import useAccountSearch from './useAccountSearch';

const ACCOUNTS = [{ label: 'Alpha Fleet', value: 7 }];

let hook;

function Probe({ fetchPage, isOpen }) {
  hook = useAccountSearch(fetchPage, isOpen);

  return (
    <div>
      <span data-testid="accounts">
        {hook.accountOptions.map((account) => account.label).join(',')}
      </span>
      <span data-testid="loading">{String(hook.isLoadingAccounts)}</span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;

const fetchPageMock = (pages, total) =>
  jest.fn((query, offset) =>
    Promise.resolve({ accounts: pages[offset] || [], total }),
  );

beforeEach(() => jest.useFakeTimers());

afterEach(() => {
  jest.useRealTimers();
  hook = undefined;
});

describe('management/shared useAccountSearch', () => {
  it('fetches nothing while closed', () => {
    const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);

    render(<Probe fetchPage={fetchPage} isOpen={false} />);

    expect(fetchPage).not.toHaveBeenCalled();
  });

  it('loads the first page when it opens', async () => {
    const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);

    render(<Probe fetchPage={fetchPage} isOpen />);

    await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));
    expect(fetchPage).toHaveBeenCalledWith('', 0);
  });

  // Without this, a caller that shows an empty state on options.length === 0
  // would flash "No accounts available" while the first page is in flight.
  it('reports loading while the first page is in flight', async () => {
    let resolveFirstPage;
    const fetchPage = jest.fn(
      () =>
        new Promise((resolve) => {
          resolveFirstPage = resolve;
        }),
    );

    render(<Probe fetchPage={fetchPage} isOpen />);

    expect(at('loading')).toEqual('true');

    resolveFirstPage({ accounts: ACCOUNTS, total: ACCOUNTS.length });
    await waitFor(() => expect(at('loading')).toEqual('false'));
  });

  it('stops loading once a failed first page settles', async () => {
    const fetchPage = jest.fn().mockRejectedValue(new Error('offline'));

    render(<Probe fetchPage={fetchPage} isOpen />);

    await waitFor(() => expect(at('loading')).toEqual('false'));
  });

  it('recovers when the caller closes while the first page is still in flight', async () => {
    let resolveFirstPage;
    let calls = 0;
    const fetchPage = jest.fn(() => {
      calls += 1;
      if (calls === 1) {
        return new Promise((resolve) => {
          resolveFirstPage = resolve;
        });
      }
      return Promise.resolve({ accounts: ACCOUNTS, total: ACCOUNTS.length });
    });

    const { rerender } = render(<Probe fetchPage={fetchPage} isOpen />);
    expect(at('loading')).toEqual('true');

    rerender(<Probe fetchPage={fetchPage} isOpen={false} />);
    expect(at('loading')).toEqual('false');

    resolveFirstPage({ accounts: [{ label: 'Stale', value: 99 }], total: 1 });
    await Promise.resolve();
    expect(at('accounts')).toEqual('');

    rerender(<Probe fetchPage={fetchPage} isOpen />);

    await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));
    expect(fetchPage.mock.calls.length).toEqual(2);
  });

  it('does not refetch when it opens again', async () => {
    const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);

    const { rerender } = render(<Probe fetchPage={fetchPage} isOpen />);
    await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));

    const calls = fetchPage.mock.calls.length;
    rerender(<Probe fetchPage={fetchPage} isOpen={false} />);
    rerender(<Probe fetchPage={fetchPage} isOpen />);

    expect(fetchPage.mock.calls.length).toEqual(calls);
  });

  it('survives a failed load', async () => {
    const fetchPage = jest.fn().mockRejectedValue(new Error('offline'));

    render(<Probe fetchPage={fetchPage} isOpen />);

    await waitFor(() => expect(fetchPage).toHaveBeenCalled());
    expect(at('accounts')).toEqual('');
  });

  it('allows a retry after a failed load', async () => {
    const failing = jest.fn().mockRejectedValue(new Error('offline'));
    const { rerender } = render(<Probe fetchPage={failing} isOpen />);
    await waitFor(() => expect(failing).toHaveBeenCalled());

    const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);
    rerender(<Probe fetchPage={fetchPage} isOpen={false} />);
    rerender(<Probe fetchPage={fetchPage} isOpen />);

    await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));
  });

  describe('searching', () => {
    it('narrows the list', async () => {
      const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);
      render(<Probe fetchPage={fetchPage} isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));

      fetchPage.mockResolvedValue({
        accounts: [{ label: 'Beta Fleet', value: 8 }],
        total: 1,
      });
      hook.searchAccounts('beta');
      jest.runOnlyPendingTimers();

      await waitFor(() => expect(at('accounts')).toEqual('Beta Fleet'));
    });

    // SearchInput fires per keystroke, so only the last term should be requested.
    it('debounces, issuing one request for a run of keystrokes', async () => {
      const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);
      render(<Probe fetchPage={fetchPage} isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));

      const before = fetchPage.mock.calls.length;
      hook.searchAccounts('b');
      hook.searchAccounts('be');
      hook.searchAccounts('bet');
      jest.runOnlyPendingTimers();

      await waitFor(() =>
        expect(fetchPage.mock.calls.length).toEqual(before + 1),
      );
      expect(fetchPage.mock.calls[before]).toEqual(['bet', 0]);
    });

    it('empties the list when a search fails', async () => {
      const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);
      render(<Probe fetchPage={fetchPage} isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));

      fetchPage.mockRejectedValue(new Error('offline'));
      hook.searchAccounts('beta');
      jest.runOnlyPendingTimers();

      await waitFor(() => expect(at('accounts')).toEqual(''));
    });

    it('drops a debounced search still pending when the caller closes', async () => {
      const fetchPage = fetchPageMock({ 0: ACCOUNTS }, ACCOUNTS.length);
      const { rerender } = render(<Probe fetchPage={fetchPage} isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha Fleet'));

      const before = fetchPage.mock.calls.length;
      hook.searchAccounts('beta');
      rerender(<Probe fetchPage={fetchPage} isOpen={false} />);
      jest.runOnlyPendingTimers();

      expect(fetchPage.mock.calls.length).toEqual(before);
    });

    it('goes back to the first page for a new search', async () => {
      const fetchPage = fetchPageMock({ 0: [{ label: 'A', value: 1 }] }, 9);
      render(<Probe fetchPage={fetchPage} isOpen />);
      await waitFor(() => expect(at('accounts')).toEqual('A'));

      fetchPage.mockResolvedValue({
        accounts: [{ label: 'Beta Fleet', value: 8 }],
        total: 1,
      });
      hook.searchAccounts('beta');
      jest.runOnlyPendingTimers();

      await waitFor(() => expect(at('accounts')).toEqual('Beta Fleet'));
      expect(fetchPage.mock.calls[0]).toEqual(['', 0]);
    });
  });

  // An admin can administer thousands, so the caller holds a page at a time.
  describe('paging', () => {
    const PAGE_ONE = [{ label: 'A', value: 1 }];
    const PAGE_TWO = [{ label: 'B', value: 2 }];

    const openWithPages = async (pages, total) => {
      const fetchPage = fetchPageMock(pages, total);
      render(<Probe fetchPage={fetchPage} isOpen />);
      await waitFor(() => expect(fetchPage).toHaveBeenCalled());
      return fetchPage;
    };

    it('reports the total and that more remain', async () => {
      await openWithPages({ 0: PAGE_ONE }, 3);

      expect(hook.accountsTotal).toEqual(3);
      expect(hook.hasMoreAccounts).toBe(true);
    });

    it('reports nothing more once the page is the whole list', async () => {
      await openWithPages({ 0: PAGE_ONE }, 1);

      expect(hook.hasMoreAccounts).toBe(false);
    });

    it('appends the next page rather than replacing it', async () => {
      await openWithPages({ 0: PAGE_ONE, 1: PAGE_TWO }, 2);

      hook.loadMoreAccounts();

      await waitFor(() => expect(at('accounts')).toEqual('A,B'));
    });

    it('asks for the next page at the offset it has reached', async () => {
      const fetchPage = await openWithPages({ 0: PAGE_ONE, 1: PAGE_TWO }, 2);
      const before = fetchPage.mock.calls.length;

      hook.loadMoreAccounts();

      await waitFor(() =>
        expect(fetchPage.mock.calls.length).toBeGreaterThan(before),
      );
      expect(fetchPage.mock.calls[before]).toEqual(['', 1]);
    });

    it('stops asking once everything is loaded', async () => {
      const fetchPage = await openWithPages({ 0: PAGE_ONE }, 1);
      const before = fetchPage.mock.calls.length;

      hook.loadMoreAccounts();

      expect(fetchPage.mock.calls.length).toEqual(before);
    });

    // A page can overlap the one before it when accounts change between the
    // two requests, and a repeated row would collide on OptionsList's keys.
    it('drops an account the previous page already carried', async () => {
      await openWithPages({ 0: PAGE_ONE, 1: [...PAGE_ONE, ...PAGE_TWO] }, 3);

      hook.loadMoreAccounts();

      await waitFor(() => expect(at('accounts')).toEqual('A,B'));
    });

    // Losing a page must not empty the list under the admin: the accounts
    // already on screen are still theirs to pick.
    it('keeps what is loaded when the next page fails', async () => {
      const fetchPage = await openWithPages({ 0: PAGE_ONE }, 3);

      fetchPage.mockRejectedValue(new Error('offline'));
      hook.loadMoreAccounts();

      await waitFor(() => expect(fetchPage).toHaveBeenCalled());
      expect(at('accounts')).toEqual('A');
    });
  });
});
